import { useState, useEffect, useRef } from 'react';
import { FinalReflection } from '../reflection/FinalReflection';
import { updateLearnerState, saveVoiceTranscript } from '@/lib/api';
import { extractSummaries } from '@/lib/summary-extractor';
import { AudioQueue, floatTo16BitPCM, pcm16ToBase64, base64ToPCM16 } from '@/lib/audio-utils';
import type { AgentConfig, LearnerState } from '@/lib/types';

interface VoiceReflectionProps {
  learnerId: string;
  moduleId: string;
  agentConfig: AgentConfig;
  learnerState: LearnerState;
  onComplete: () => void;
}

interface TranscriptItem {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

type Phase =
  | 'init'
  | 'requesting-mic'
  | 'connecting'
  | 'greeting'
  | 'conversation'
  | 'complete'
  | 'error';

/**
 * Grace period (ms) after AI finishes speaking before we resume sending
 * mic audio.  Allows speaker echo to die off so the server VAD doesn't
 * interpret residual playback as user speech.
 */
const ECHO_GUARD_MS = 800;

export function VoiceReflection({
  learnerId,
  moduleId,
  agentConfig,
  learnerState,
  onComplete,
}: VoiceReflectionProps) {
  const [useTextFallback, setUseTextFallback] = useState(false);
  const [phase, setPhase] = useState<Phase>('init');
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [transcript, setTranscript] = useState<TranscriptItem[]>([]);
  const [summaries, setSummaries] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);

  // Snapshot of transcript at the moment of switching to text mode
  const [frozenTranscript, setFrozenTranscript] = useState<TranscriptItem[] | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioQueueRef = useRef<AudioQueue | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const animFrameRef = useRef<number>(0);
  const isMutedRef = useRef(false);
  const cleanedUpRef = useRef(false);

  // ── Echo-suppression refs ──
  // When true, the mic processor will NOT send audio to the WebSocket.
  // This prevents the mic from picking up the AI's speaker output and
  // feeding it back as "user speech", which causes the infinite loop.
  const micGatedRef = useRef(false);
  const echoGuardTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep muted ref in sync
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  // Initialize audio queue
  useEffect(() => {
    audioQueueRef.current = new AudioQueue();
    return () => {
      audioQueueRef.current?.clear();
    };
  }, []);

  // Auto-scroll transcript
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcript, isSpeaking]);

  // Start the entire flow on mount
  useEffect(() => {
    initializeVoice();
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Helpers: gate / un-gate the microphone ──

  /** Immediately stop forwarding mic audio (AI is about to speak). */
  const gateMic = () => {
    if (echoGuardTimerRef.current) {
      clearTimeout(echoGuardTimerRef.current);
      echoGuardTimerRef.current = null;
    }
    micGatedRef.current = true;
  };

  /**
   * Schedule mic re-opening after a grace period.
   * Also flushes the server-side input audio buffer so any echo that
   * leaked in while the AI was speaking doesn't trigger a new response.
   */
  const ungateMicAfterDelay = () => {
    // Flush any echo that accumulated in the server buffer
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'input_audio_buffer.clear' }));
    }

    echoGuardTimerRef.current = setTimeout(() => {
      micGatedRef.current = false;
      echoGuardTimerRef.current = null;
    }, ECHO_GUARD_MS);
  };

  // ── Voice initialization ──

  const initializeVoice = async () => {
    setPhase('requesting-mic');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      mediaStreamRef.current = stream;
      setPhase('connecting');
      connectWebSocket();
    } catch (err) {
      console.error('[Voice] Mic denied:', err);
      setError('Microphone access is needed for voice reflection.');
      setPhase('error');
    }
  };

  const connectWebSocket = () => {
    // WebSocket connections must go directly to the Worker, not Pages
    // Pages doesn't support WebSocket upgrades
    const isDev = window.location.host.includes('localhost');
    const protocol = isDev ? 'ws:' : 'wss:';
    const host = isDev
      ? 'localhost:8787'
      : 'itari-training-worker.aayou-ai-life.workers.dev';
    const wsUrl = `${protocol}//${host}/api/voice/session?learnerId=${learnerId}&moduleId=${moduleId}&instructions=${encodeURIComponent(
      agentConfig.systemPrompt
    )}&voice=shimmer`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[Voice] WebSocket connected');
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        handleRealtimeMessage(message);
      } catch (err) {
        console.error('[Voice] Parse error:', err);
      }
    };

    ws.onerror = () => {
      console.error('[Voice] WebSocket error');
      setError('Connection failed. Switching to text mode.');
      setPhase('error');
      setTimeout(() => setUseTextFallback(true), 2000);
    };

    ws.onclose = () => {
      console.log('[Voice] WebSocket closed');
    };
  };

  // ── Message handler ──

  const handleRealtimeMessage = (message: { type: string; [key: string]: unknown }) => {
    switch (message.type) {
      case 'session.created':
        console.log('[Voice] Session created');
        break;

      case 'session.updated':
        console.log('[Voice] Session configured — requesting greeting');
        setPhase('greeting');
        // Gate the mic before the greeting so we don't echo the AI's first words
        gateMic();
        startContinuousAudio();
        triggerGreeting();
        break;

      case 'input_audio_buffer.speech_started':
        setIsUserSpeaking(true);
        break;

      case 'input_audio_buffer.speech_stopped':
        setIsUserSpeaking(false);
        break;

      case 'conversation.item.created': {
        const item = (message as { item?: { type?: string; role?: string; content?: { transcript?: string }[] } }).item;
        if (item?.type === 'message' && item.role === 'user') {
          const content = item.content?.[0]?.transcript || '';
          if (content) {
            setTranscript((prev) => [
              ...prev,
              { role: 'user', content, timestamp: Date.now() },
            ]);
          }
        }
        break;
      }

      // ── AI audio playback ──

      case 'response.audio.delta': {
        const delta = (message as { delta?: string }).delta;
        if (delta) {
          // Gate the mic whenever we receive AI audio
          gateMic();
          setIsSpeaking(true);
          if (phase === 'greeting') setPhase('conversation');
          const pcm16 = base64ToPCM16(delta);
          audioQueueRef.current?.addChunk(pcm16);
        }
        break;
      }

      case 'response.audio.done':
        // Audio data done streaming but AudioQueue may still be playing.
        // We keep the mic gated — response.done will un-gate with a delay.
        setIsSpeaking(false);
        break;

      case 'response.done': {
        setIsSpeaking(false);

        // Re-open the mic after a grace period for echo to die off,
        // and flush the server-side audio buffer of any leaked echo.
        ungateMicAfterDelay();

        const resp = message as {
          response?: {
            output?: Array<{
              type?: string;
              role?: string;
              content?: Array<{ transcript?: string; text?: string }>;
            }>;
          };
        };
        const output = resp.response?.output?.[0];
        if (output?.type === 'message' && output.role === 'assistant') {
          const content =
            output.content?.[0]?.transcript ||
            output.content?.[0]?.text ||
            '';
          if (content) {
            const { cleanedMessage, summaries: extracted } =
              extractSummaries(content);

            setTranscript((prev) => [
              ...prev,
              {
                role: 'assistant',
                content: cleanedMessage,
                timestamp: Date.now(),
              },
            ]);

            if (extracted.length > 0) {
              setSummaries((prev) => [...prev, ...extracted]);
              setPhase('complete');
            }
          }
        }
        break;
      }

      case 'error': {
        const err = (message as { error?: { message?: string } }).error;
        console.error('[Voice] API error:', err);
        setError(err?.message || 'An error occurred');
        break;
      }
    }
  };

  const triggerGreeting = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: 'response.create',
          response: { modalities: ['text', 'audio'] },
        })
      );
    }
  };

  // ── Continuous audio capture ──

  const startContinuousAudio = () => {
    if (!mediaStreamRef.current) return;

    const audioContext = new AudioContext({ sampleRate: 24000 });
    audioContextRef.current = audioContext;

    const source = audioContext.createMediaStreamSource(mediaStreamRef.current);

    // Analyser for audio level visualization
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    analyserRef.current = analyser;
    source.connect(analyser);

    // Start audio level monitoring
    monitorAudioLevel();

    // ScriptProcessorNode for continuous audio streaming
    const processor = audioContext.createScriptProcessor(4096, 1, 1);
    processorRef.current = processor;

    processor.onaudioprocess = (e) => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
      if (isMutedRef.current) return;

      // ── Echo suppression: skip sending audio while AI is speaking
      //    or during the post-speech grace period.
      if (micGatedRef.current) return;

      const inputData = e.inputBuffer.getChannelData(0);
      const pcm16 = floatTo16BitPCM(inputData);
      const base64Audio = pcm16ToBase64(pcm16);

      wsRef.current.send(
        JSON.stringify({
          type: 'input_audio_buffer.append',
          audio: base64Audio,
        })
      );
    };

    source.connect(processor);
    processor.connect(audioContext.destination);
  };

  const monitorAudioLevel = () => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

    const tick = () => {
      if (!analyserRef.current) return;
      analyserRef.current.getByteFrequencyData(dataArray);

      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i];
      }
      const avg = sum / dataArray.length / 255;
      setAudioLevel(isMutedRef.current ? 0 : avg);

      animFrameRef.current = requestAnimationFrame(tick);
    };
    tick();
  };

  // ── Cleanup ──

  const cleanup = () => {
    if (cleanedUpRef.current) return;
    cleanedUpRef.current = true;

    if (echoGuardTimerRef.current) {
      clearTimeout(echoGuardTimerRef.current);
    }
    cancelAnimationFrame(animFrameRef.current);
    if (wsRef.current) {
      try { wsRef.current.close(); } catch { /* ignore */ }
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
    }
    if (processorRef.current) {
      try { processorRef.current.disconnect(); } catch { /* ignore */ }
    }
    if (audioContextRef.current) {
      try { audioContextRef.current.close(); } catch { /* ignore */ }
    }
    // Stop any queued/playing audio immediately
    if (audioQueueRef.current) {
      audioQueueRef.current.clear();
    }
  };

  // ── Persist transcript to backend (fire-and-forget) ──

  const persistTranscript = (items: TranscriptItem[]) => {
    if (items.length === 0) return;
    const messages = items.map(({ role, content }) => ({ role, content }));
    saveVoiceTranscript(learnerId, moduleId, messages).catch((err) =>
      console.error('[Voice] Failed to save transcript:', err)
    );
  };

  // ── User actions ──

  const handleEnd = async () => {
    cleanup();

    // Persist transcript for JSON export
    persistTranscript(transcript);

    if (summaries.length > 0) {
      try {
        await updateLearnerState({
          learnerId,
          moduleId,
          finalReflectionUpdate: {
            synthesis: summaries.join(' '),
            completed: true,
          },
        });
      } catch (err) {
        console.error('[Voice] Failed to save:', err);
      }
    }

    onComplete();
  };

  const handleSkip = async () => {
    cleanup();

    // Persist transcript for JSON export
    persistTranscript(transcript);

    try {
      await updateLearnerState({
        learnerId,
        moduleId,
        finalReflectionUpdate: {
          synthesis: summaries.join(' ') || 'Voice reflection skipped',
          completed: false,
        },
      });
    } catch (err) {
      console.error('[Voice] Failed to save skip:', err);
    }

    onComplete();
  };

  const handleSwitchToText = () => {
    cleanup();
    // Persist transcript for JSON export
    persistTranscript(transcript);
    // Freeze the current transcript so FinalReflection can display it
    setFrozenTranscript([...transcript]);
    setUseTextFallback(true);
  };

  const toggleMute = () => {
    setIsMuted((prev) => !prev);
  };

  // ─── Text fallback ───
  if (useTextFallback) {
    return (
      <FinalReflection
        learnerId={learnerId}
        moduleId={moduleId}
        learnerState={learnerState}
        onComplete={onComplete}
        initialTranscript={
          frozenTranscript
            ? frozenTranscript.map(({ role, content }) => ({ role, content }))
            : undefined
        }
      />
    );
  }

  // ─── Derived state ───
  const statusText = (() => {
    switch (phase) {
      case 'init':
      case 'requesting-mic':
        return 'Requesting microphone access...';
      case 'connecting':
        return 'Connecting...';
      case 'greeting':
        return 'Starting conversation...';
      case 'conversation':
        if (isMuted) return 'Microphone muted';
        if (isSpeaking) return 'AI is speaking...';
        if (isUserSpeaking) return 'Listening to you...';
        return 'Listening — speak whenever you\'re ready';
      case 'complete':
        return 'Reflection complete';
      case 'error':
        return error || 'Something went wrong';
    }
  })();

  // Orb animation scale based on state
  const orbScale = (() => {
    if (isSpeaking) return 1.15;
    if (isUserSpeaking) return 1.0 + audioLevel * 0.3;
    if (isMuted) return 0.85;
    return 1.0 + audioLevel * 0.15;
  })();

  const orbColor = (() => {
    if (phase === 'error') return 'from-red-400 to-red-600';
    if (phase === 'complete') return 'from-emerald-400 to-emerald-600';
    if (isMuted) return 'from-slate-300 to-slate-400 dark:from-slate-600 dark:to-slate-700';
    if (isSpeaking) return 'from-indigo-400 to-violet-500';
    if (isUserSpeaking) return 'from-emerald-400 to-teal-500';
    return 'from-sky-400 to-indigo-500';
  })();

  return (
    <div className="max-w-2xl mx-auto py-6 px-4 flex flex-col" style={{ minHeight: 'calc(100vh - 200px)' }}>
      {/* Header — compact badge */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400 text-xs font-medium">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
          Collaborative Voice Reflection
        </div>
      </div>

      {/* Animated Orb — central focus */}
      <div className="flex flex-col items-center mb-6">
        <div className="relative w-28 h-28 flex items-center justify-center">
          {/* Outer pulse ring */}
          {(isSpeaking || isUserSpeaking) && !isMuted && (
            <div
              className={`absolute inset-0 rounded-full bg-gradient-to-br ${orbColor} opacity-20 animate-ping`}
              style={{ animationDuration: '1.5s' }}
            />
          )}

          {/* Mid ring */}
          <div
            className={`absolute inset-2 rounded-full bg-gradient-to-br ${orbColor} opacity-15 transition-transform duration-300`}
            style={{ transform: `scale(${orbScale * 1.05})` }}
          />

          {/* Core orb */}
          <div
            className={`relative w-20 h-20 rounded-full bg-gradient-to-br ${orbColor} shadow-lg transition-transform duration-200 flex items-center justify-center`}
            style={{ transform: `scale(${orbScale})` }}
          >
            {/* Icon inside orb */}
            {isMuted ? (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-80">
                <line x1="1" y1="1" x2="23" y2="23" />
                <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .76-.13 1.5-.36 2.18" />
              </svg>
            ) : isSpeaking ? (
              <div className="flex gap-1 items-end h-7">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-1 bg-white/80 rounded-full animate-pulse"
                    style={{
                      height: `${12 + Math.sin(Date.now() / 200 + i) * 8}px`,
                      animationDelay: `${i * 0.1}s`,
                      animationDuration: '0.6s',
                    }}
                  />
                ))}
              </div>
            ) : (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-80">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              </svg>
            )}
          </div>
        </div>

        {/* Status text */}
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-3 text-center">
          {statusText}
        </p>
      </div>

      {/* Error display */}
      {error && phase === 'error' && (
        <div className="mb-4 px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-center">
          <p className="text-sm text-red-700 dark:text-red-300 mb-2">{error}</p>
          <button
            onClick={handleSwitchToText}
            className="text-sm text-red-600 dark:text-red-400 underline hover:no-underline"
          >
            Switch to text mode
          </button>
        </div>
      )}

      {/* Transcript — grows as conversation happens */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto space-y-3 mb-6 min-h-[120px]"
      >
        {transcript.length === 0 && phase !== 'error' && (
          <div className="text-center py-8 text-slate-400 dark:text-slate-500 text-sm">
            {phase === 'greeting' || phase === 'conversation'
              ? 'Conversation will appear here...'
              : 'Setting up voice connection...'}
          </div>
        )}

        {transcript.map((item, idx) => (
          <div
            key={idx}
            className={`flex ${item.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                item.role === 'user'
                  ? 'bg-indigo-500 text-white rounded-br-md'
                  : 'bg-slate-100 dark:bg-slate-700/80 text-slate-800 dark:text-slate-200 rounded-bl-md'
              }`}
            >
              <p className="text-sm leading-relaxed whitespace-pre-wrap">
                {item.content}
              </p>
              <div className={`text-[10px] mt-1 ${
                item.role === 'user' ? 'text-indigo-200' : 'text-slate-400 dark:text-slate-500'
              }`}>
                {item.role === 'user' ? 'You' : 'AI'} · {new Date(item.timestamp).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
            </div>
          </div>
        ))}

        {/* Speaking indicator */}
        {isSpeaking && (
          <div className="flex justify-start">
            <div className="bg-slate-100 dark:bg-slate-700/80 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex gap-1.5 items-center">
                <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" />
                <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Completion banner */}
      {phase === 'complete' && (
        <div className="mb-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-5 text-center">
          <p className="text-emerald-800 dark:text-emerald-200 font-medium text-sm mb-1">
            Reflection complete — your insights have been captured.
          </p>
          <p className="text-emerald-600 dark:text-emerald-400 text-xs">
            Click "Finish" below to save and continue.
          </p>
        </div>
      )}

      {/* Bottom controls — always visible */}
      <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
        <div className="flex items-center justify-between">
          {/* Left: mute + text mode */}
          <div className="flex items-center gap-3">
            {/* Mute toggle */}
            <button
              onClick={toggleMute}
              disabled={phase === 'error' || phase === 'complete'}
              title={isMuted ? 'Unmute microphone' : 'Mute microphone'}
              className={`p-2.5 rounded-full transition-all ${
                isMuted
                  ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
              } disabled:opacity-40`}
            >
              {isMuted ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="1" y1="1" x2="23" y2="23" />
                  <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                  <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .76-.13 1.5-.36 2.18" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              )}
            </button>

            {/* Audio level bar (when not muted) */}
            {!isMuted && phase === 'conversation' && (
              <div className="flex gap-0.5 items-end h-4">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="w-1 rounded-full bg-emerald-400 transition-all duration-100"
                    style={{
                      height: `${Math.max(3, Math.min(16, audioLevel * 60 - i * 3))}px`,
                    }}
                  />
                ))}
              </div>
            )}

            {/* Text mode link */}
            <button
              onClick={handleSwitchToText}
              className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
            >
              Switch to text
            </button>
          </div>

          {/* Right: end / skip / finish */}
          <div className="flex items-center gap-3">
            {phase === 'complete' ? (
              <button
                onClick={handleEnd}
                className="px-5 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-medium rounded-lg text-sm transition-all"
              >
                Finish
              </button>
            ) : (
              <>
                <button
                  onClick={handleSkip}
                  className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                >
                  Skip
                </button>
                <button
                  onClick={handleEnd}
                  className="px-4 py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-medium transition-colors"
                >
                  End Session
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
